import {
  PublicBaseEventSerializer,
  PublicCalendarEventDetailsSerializer,
} from "@shared/modules/events/types/public";
import { ExtendedAxiosRequestConfig } from "@shared/services/api/abstracts/Repository";
import { useModelService } from "@shared/services/api/hooks/useModelService";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { useMemo } from "react";
import { UseQueryOptions, UseQueryResult } from "react-query";
import PublicBaseEventRepository from "../repositories/PublicBaseEventRepository";

function usePublicEventService() {
  const repository = useMemo(() => {
    return new PublicBaseEventRepository();
  }, []);

  const { useGenericQuery, useRetrieve, ...rest } = useModelService(repository);

  const exchangeCredit = isStorageAvailable("localStorage")
    ? localStorage.getItem("exchangeCreditData")
    : null;

  let transformData = false;
  if (exchangeCredit) {
    const exchangeCreditData = JSON.parse(exchangeCredit);
    const now = new Date();
    if (now.getTime() > exchangeCreditData.expiry_time) {
      localStorage.removeItem("exchangeCreditData");
    } else {
      transformData = exchangeCreditData?.is_membership_ticket_exchange;
    }
  }

  /**
   * Retrieves event data with conditional ticket type transformation.
   *
   * This hook extends the base retrieval functionality by conditionally replacing
   * standard ticket_types with membership_ticket_types when the user is in an
   * itemized membership ticket exchange flow.
   *
   * The transformation occurs when:
   * 1. Valid exchangeCreditData exists in localStorage
   * 2. The exchange credit has not expired (based on expiry_time)
   * 3. The is_membership_ticket_exchange flag is true in the exchange credit data
   *
   * This conditional transformation ensures that:
   * - Membership ticket types are correctly displayed on the event detail page and seating map/seat selection modal
   * - The appropriate ticket types are used during the itemized exchange process
   * - Users see consistent ticket options throughout their exchange journey
   */
  function useRetrieveWithMembershipTicketTypesOverride(
    slug: string | number | undefined,
    {
      config,
      options,
    }: {
      config?: ExtendedAxiosRequestConfig;
      options?: UseQueryOptions<
        PublicBaseEventSerializer,
        unknown,
        PublicBaseEventSerializer,
        string[]
      >;
    } = {}
  ): UseQueryResult<PublicBaseEventSerializer> {
    const result = useRetrieve(slug, {
      config,
      options: {
        ...options,
        enabled: Boolean(slug) || options?.enabled,
      },
    });

    // Transform the data if it exists and has membership_ticket_types
    if (result.isSuccess && result.data && transformData) {
      const transformedData = result.data.membership_ticket_types
        ? {
            ...result.data,
            ticket_types: result.data.membership_ticket_types,
          }
        : result.data;

      return {
        ...result,
        data: transformedData,
      };
    }
    return result;
  }

  function useCheckEventPassword(eventSlug: string) {
    return useGenericQuery("checkEventPassword", {
      injects: {
        eventSlug,
      },
    });
  }

  function useCheckEventPasswordAuthorized(eventSlug: string) {
    return useGenericQuery("checkEventPasswordAuthorized", {
      injects: {
        eventSlug,
      },
    });
  }

  function usePublicCalendarEventDetails(
    eventSlug: string | undefined,
    {
      params,
      config,
      options,
    }: {
      params?: Record<string, unknown>;
      config?: ExtendedAxiosRequestConfig;
      options?: UseQueryOptions<PublicCalendarEventDetailsSerializer>;
    } = {}
  ) {
    return useGenericQuery("calendarEventDetails", {
      injects: {
        eventSlug,
      },
      params: {
        version: 1,
        ...params,
      },
      options: {
        enabled: Boolean(eventSlug),
        ...options,
      },
      config,
    });
  }

  return {
    useRetrieve,
    useRetrieveWithMembershipTicketTypesOverride,
    useGenericQuery,
    useCheckEventPassword,
    useCheckEventPasswordAuthorized,
    usePublicCalendarEventDetails,
    ...rest,
  };
}

export default usePublicEventService;

export type UsePublicEventService = ReturnType<typeof usePublicEventService>;
